import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { of, Subject } from 'rxjs';
import {
  catchError, distinctUntilChanged, map, startWith, switchMap, takeUntil,
} from 'rxjs/operators';
import * as moment from 'moment';

import * as fromRoot from '../../../../../../../state/app.state';
import { projectDetails, projectsFilterList, sitesFilterList } from '../../../../../core/projects.selectors';
import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AuthService } from '../../../../../../../shared/services';
import {
  DIARY_DATE_PARAM, DIARY_ID_PARAM, DIARY_SECTION_PARAM, DIARY_SITE_PARAM,
} from '../daily-diary-mode.util';

/**
 * Creating a diary mirrors `CreateDailyReportComponent`: the same dialog box,
 * the same Project / Site / Date fields, and the same "create it, then fill it
 * in" flow — weather and remarks are added afterwards from the record's own
 * component sections rather than stacked into one long create form.
 *
 * Project and Site are always known here (the diary's + only exists on a
 * site's Daily diary tab), so they come through prefilled, exactly as the
 * report's dialog does when opened from a site page.
 */
@Component({
  selector: 'app-create-daily-diary',
  templateUrl: './create-daily-diary.component.html',
  styleUrls: ['./create-daily-diary.component.scss'],
  standalone: false,
})
export class CreateDailyDiaryComponent implements OnInit, OnDestroy {
  errorMessages: any = {};
  errorMessageMap = {
    project: { required: 'This field is required.' },
    site: { required: 'This field is required.' },
    diary_date: { required: 'This field is required.' },
  };

  projectsList = [];
  sitesList = [];
  saving = false;

  /** Set when this user already has a diary on the chosen date — see below. */
  existingDiaryId: string | null = null;

  form = this.fb.group({
    project: new FormControl({ value: null, disabled: false }, [Validators.required]),
    site: new FormControl({ value: null, disabled: false }, [Validators.required]),
    diary_date: new FormControl({ value: null, disabled: false }, [Validators.required]),
  });

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private store: Store<fromRoot.State>,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private authService: AuthService,
    private dialogRef: MatDialogRef<CreateDailyDiaryComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { projectId: string; siteId: string; date?: string },
  ) {
    this.store.select(projectsFilterList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(list => {
        this.projectsList = list?.length ? list : this.projectsList;
      });

    // The report's dialog is usually opened from a project page, where those
    // filter lists are already loaded. The diary's + lives on a site page,
    // where they can be empty — the dropdowns would then show "Select a
    // project" over a value that is actually set. Fall back to the project in
    // the store so both fields read the way the report's do.
    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((project: any) => {
        if (!project?.id) return;
        if (!this.projectsList?.length) {
          this.projectsList = [{ value: project.id, label: project.name }];
        }
        if (!this.sitesList?.length) {
          this.sitesList = (project.sites || []).map((s: any) => ({ value: s.id, label: s.name }));
        }
      });
  }

  ngOnInit(): void {
    // Both are fixed by where the + lives, so lock the project the same way the
    // report's dialog locks it when launched from a project page.
    this.form.controls.project.setValue(this.data?.projectId);
    this.form.controls.project.disable({ emitEvent: false });
    this.form.controls.site.setValue(this.data?.siteId);
    this.form.controls.site.disable({ emitEvent: false });
    this.form.controls.diary_date.setValue(
      this.data?.date ? moment(this.data.date, 'YYYY-MM-DD') : null,
    );

    this.store.select(sitesFilterList, { projectId: this.data?.projectId })
      .pipe(
        takeUntil(this.onDestroy),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      )
      .subscribe(list => {
        this.sitesList = list?.length ? list : this.sitesList;
      });

    // switchMap, not a fresh subscribe per change: without cancellation a
    // slow response for an earlier date can land last and describe the wrong
    // day — blocking Create on a free date or allowing it on a taken one.
    this.form.controls.diary_date.valueChanges
      .pipe(
        startWith(this.form.controls.diary_date.value),
        switchMap(value => this.lookupExistingDiary(value)),
        takeUntil(this.onDestroy),
      )
      .subscribe(diaryId => { this.existingDiaryId = diaryId; });
  }

  /**
   * The api's `addDraft` looks up a diary by site + date + author and returns
   * that one rather than inserting a second, so Create on an occupied date
   * would quietly hand back an existing record — possibly one already
   * submitted and no longer editable. The report's dialog warns about exactly
   * this, so warn the same way and offer to open it instead.
   */
  private lookupExistingDiary(value: moment.Moment | string | null) {
    if (!value || !this.data?.siteId) return of(null);
    const date = moment(value).format('YYYY-MM-DD');
    const accountId = this.authService.getCurrentUser()?.id;

    return this.projectsService.fetchDailyDiaries({
      siteId: this.data.siteId,
      startDate: date,
      endDate: date,
    }).pipe(
      map((resp: any) => {
        const mine = (resp?.data || [])
          .map((r: any) => ({ ...r, ...(r.attributes || {}) }))
          .find((r: any) => (r?.createdBy?.id || r?.created_by?.id) === accountId);
        return mine ? (mine.id || mine.uuid) : null;
      }),
      // Don't block creating on a failed lookup — the api still refuses to
      // make a duplicate, so the worst case is the old silent behaviour.
      catchError(() => of(null)),
    );
  }

  checkForErrors(currentField?: string): void {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  goToDiary(): void {
    this.navigateToDiary(
      moment(this.form.getRawValue().diary_date).format('YYYY-MM-DD'),
      this.existingDiaryId,
    );
  }

  onSubmit(): void {
    if (this.form.invalid || this.saving) return;
    this.saving = true;

    const value = this.form.getRawValue();
    const diaryDate = moment(value.diary_date).format('YYYY-MM-DD');

    // Seed the day's conditions the way the mobile app does: it reads
    // /weather for the site and date and sends the result with the diary.
    // The api stores whatever weather it is handed, so the client is the one
    // that fills it in — keeping one source rather than two. A lookup that
    // fails or returns nothing just creates the diary without weather, which
    // is then filled in from its Weather panel.
    this.projectsService.fetchSiteWeather({ siteId: value.site, date: diaryDate })
      .pipe(
        catchError(() => of(null)),
        switchMap(resp => this.projectsService.saveDailyDiaryDraft({
          siteId: value.site,
          data: {
            diary_date: diaryDate,
            // Not asked for at creation — a diary starts as a work day and the
            // flag is flipped from the record's Work day panel.
            contractor_working: true,
            ...(resp?.weather ? { weather: resp.weather } : {}),
          },
        })),
        takeUntil(this.onDestroy),
      )
      .subscribe({
        next: (resp: any) => {
          const node = resp?.data || resp;
          const saved = node?.attributes ? { id: node.id, ...node.attributes } : node;
          this.saving = false;
          this.projectsService.dailyDiaryChanged.next();
          this.navigateToDiary(diaryDate, saved?.id || saved?.uuid || null);
        },
        error: (err: any) => {
          this.saving = false;
          this.commonService.errorNotification(err?.error?.error || 'Failed to create diary');
        },
      });
  }

  /** Land on the record in detail mode, where the components are filled in. */
  private navigateToDiary(diaryDate: string, diaryId: string | null): void {
    this.router.navigate([], {
      queryParams: {
        // The site MUST travel: the project list spans every site, so on a
        // fresh load the record view has no site of its own to fall back on
        // and renders "No diaries available for the selected date" over the
        // diary just created.
        [DIARY_SITE_PARAM]: this.form.getRawValue().site || this.data?.siteId,
        [DIARY_DATE_PARAM]: diaryDate,
        [DIARY_ID_PARAM]: diaryId,
        [DIARY_SECTION_PARAM]: null,
      },
      queryParamsHandling: 'merge',
    }).then(() => this.dialogRef.close());
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
